/**
 * Error handling utilities for safely extracting error information
 */

/**
 * Safely extracts an error message from an unknown error value.
 * Handles Error instances, objects with message or detail property, and primitive values.
 * Checks for 'detail' property (common in API error responses) after checking 'message'.
 *
 * @param error - The error value (can be Error, object, string, etc.)
 * @returns A string representation of the error message
 *
 * @example
 * ```typescript
 * extractErrorMessage(new Error('Something went wrong')) // 'Something went wrong'
 * extractErrorMessage({ message: 'Custom error' }) // 'Custom error'
 * extractErrorMessage({ detail: 'API error detail' }) // 'API error detail'
 * extractErrorMessage('String error') // 'String error'
 * extractErrorMessage(null) // 'null'
 * ```
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object') {
    // Check for 'message' property first (most common)
    if ('message' in error && error.message != null) {
      return String(error.message);
    }
    // Check for 'detail' property (common in API error responses)
    if ('detail' in error && error.detail != null) {
      return String(error.detail);
    }
  }
  if (typeof error === 'string') {
    return error;
  }
  return String(error);
}

/**
 * Safely extracts error information for logging purposes.
 * Returns both the error message and the Error instance (if available).
 *
 * @param error - The error value (can be Error, object, string, etc.)
 * @returns An object with the error message and optional Error instance
 *
 * @example
 * ```typescript
 * const { message, errorInstance } = extractErrorForLogging(new Error('Failed'));
 * logger.error(message, {}, errorInstance);
 * ```
 */
export function extractErrorForLogging(error: unknown): {
  message: string;
  errorInstance?: Error;
} {
  return {
    message: extractErrorMessage(error),
    errorInstance: error instanceof Error ? error : undefined,
  };
}

/**
 * Determines if an error is a network-level error (Failed to fetch, CORS, etc.)
 *
 * Network errors can occur due to:
 * - Network connectivity issues
 * - CORS errors
 * - SSL/TLS certificate issues
 * - Server not responding
 * - Request being blocked by browser security
 *
 * @param error - Error to check
 * @returns True if error is a network error
 *
 * @example
 * ```typescript
 * try {
 *   await api.getCurrentUser();
 * } catch (error) {
 *   if (isNetworkError(error)) {
 *     // Handle network error
 *   }
 * }
 * ```
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message === 'failed to fetch' ||
    message.includes('network') ||
    message.includes('cors') ||
    message.includes('fetch')
  );
}

/**
 * OAuth error classification for user-friendly error messages.
 * Classifies errors into timeout, network, or generic categories
 * and provides appropriate user-facing messages.
 *
 * @param error - Error to classify (can be Error, string, or unknown)
 * @returns Object with error type and user-friendly message
 *
 * @example
 * ```typescript
 * const { type, message } = classifyOAuthError(new Error('Request timeout'));
 * // { type: 'timeout', message: 'The request timed out. Please check your connection and try again.' }
 *
 * const { type, message } = classifyOAuthError(new Error('Failed to fetch'));
 * // { type: 'network', message: 'Network error. Please check your connection and try again.' }
 * ```
 */
export function classifyOAuthError(error: unknown): {
  type: 'timeout' | 'network' | 'generic';
  message: string;
} {
  const errorMessage = extractErrorMessage(error).toLowerCase();

  // Check for timeout errors
  if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
    return {
      type: 'timeout',
      message: 'The request timed out. Please check your connection and try again.',
    };
  }

  // Check for network errors (including fetch failures)
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('cors')
  ) {
    return {
      type: 'network',
      message: 'Network error. Please check your connection and try again.',
    };
  }

  // Generic error - use the original message if available
  return {
    type: 'generic',
    message: extractErrorMessage(error) || 'An unexpected error occurred. Please try again.',
  };
}
